import { CalendarClock, Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge, type BadgeVariant } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import { instantToWallTime } from "../../event-experience/agenda-schedule";
import type { FeedbackForm } from "../../feedback/schemas";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import {
  findForm,
  formLabel,
  scheduledFormWarning,
} from "../announcement-form";
import {
  deletableSendCommandId,
  formatSendTime,
  parseSendTime,
  type ScheduledAnnouncement,
  type ScheduledStatus,
  useScheduledAnnouncements,
} from "../scheduled-announcements";
import { templateProblem } from "../announcement-placeholders";
import { useAdminCommand } from "../use-admin-command";
import { TimeField } from "../WallTimeField";
import {
  DeleteForEveryoneDialog,
  DeletionActions,
} from "./DeleteForEveryoneDialog";
import { PersonalisedBadge, TemplateText } from "./TemplateText";
import { FeedbackFormPicker, NO_FORM } from "./FeedbackFormPicker";
import { useSeatings } from "../../seatings/use-seatings";
import { NO_SEATING, SeatingPicker, usesDinnerTable } from "./SeatingPicker";
import { SendTimeNote } from "./SendTimeNote";
import { statusChipClass } from "./status-chip";
import { WarningNote } from "./WarningNote";

const STATUS: Record<
  ScheduledStatus,
  { label: string; variant: BadgeVariant }
> = {
  PENDING: { label: "Scheduling…", variant: "neutral" },
  SCHEDULED: { label: "Scheduled", variant: "info" },
  SENDING: { label: "Sending", variant: "warning" },
  SENT: { label: "Sent", variant: "success" },
  CANCELLED: { label: "Cancelled", variant: "neutral" },
  FAILED: { label: "Failed", variant: "error" },
};

/** 44px tall on a phone, the smallest target a thumb hits reliably. */
const ROW_BUTTON = "h-11 sm:h-9";

/**
 * Announcements booked for later, and what became of them.
 *
 * Only a SCHEDULED one can be edited or cancelled. Once the scheduler has
 * claimed it (SENDING) the text is already on its way, and the backend
 * refuses a late edit with a sentence the feedback strip shows.
 *
 * A booked announcement that links a feedback form names it, and warns while
 * the form would stop it going out (deleted, closed, or outside its window at
 * the send time). The warning clears by itself once the form is open then.
 */
export function ScheduledAnnouncementList({
  eventId,
  uid,
  timeZone,
  forms = null,
}: {
  eventId: string;
  uid: string;
  timeZone: string | undefined;
  /** The event's feedback forms; null while loading. */
  forms?: FeedbackForm[] | null;
}) {
  const { items, error } = useScheduledAnnouncements(eventId);
  const [editing, setEditing] = useState<ScheduledAnnouncement | null>(null);
  const [cancelling, setCancelling] = useState<ScheduledAnnouncement | null>(
    null,
  );
  const [deleting, setDeleting] = useState<{
    item: ScheduledAnnouncement;
    sendCommandId: string;
  } | null>(null);

  if (error) {
    return (
      <p
        className="text-sm text-destructive-text"
        data-testid="scheduled-error"
      >
        Could not load scheduled announcements: {error}
      </p>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="scheduled-announcements">
      <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <CalendarClock className="h-4 w-4 shrink-0" />
        Scheduled announcements
      </h3>
      <ul className="space-y-2">
        {items.map((item) => {
          const deleted = item.deletion?.state === "deleted";
          const sendCommandId = deletableSendCommandId(item);
          return (
            <li
              key={item.id}
              className="rounded-lg border border-border bg-card p-3 text-card-foreground"
              data-testid="scheduled-announcement"
              data-status={item.status}
              data-id={item.id}
              data-deletion={item.deletion?.state ?? "none"}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p
                    className={`break-words font-medium ${deleted ? "text-muted-foreground line-through" : "text-foreground"}`}
                  >
                    <TemplateText text={item.title} />
                  </p>
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="scheduled-send-at"
                  >
                    {formatSendTime(item.sendAt, timeZone)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <PersonalisedBadge title={item.title} body={item.body} />
                  <Badge
                    variant={deleted ? "neutral" : STATUS[item.status].variant}
                    className={statusChipClass(
                      deleted ? "neutral" : STATUS[item.status].variant,
                    )}
                    data-testid="scheduled-status"
                  >
                    {deleted ? "Deleted" : STATUS[item.status].label}
                  </Badge>
                </div>
              </div>
              {item.feedbackFormId && (
                <p
                  className="mt-1 break-words text-sm text-muted-foreground"
                  data-testid="scheduled-form"
                >
                  Feedback form: {formLabel(forms, item.feedbackFormId)}
                </p>
              )}
              {!deleted &&
                forms !== null &&
                item.feedbackFormId &&
                (item.status === "SCHEDULED" || item.status === "PENDING") &&
                (() => {
                  const warning = scheduledFormWarning(
                    findForm(forms, item.feedbackFormId),
                    item.sendAt,
                  );
                  return warning ? (
                    <WarningNote
                      className="mt-2"
                      testId="scheduled-form-warning"
                    >
                      {warning}
                    </WarningNote>
                  ) : null;
                })()}
              {!deleted &&
                item.status === "SENT" &&
                item.recipientCount !== undefined && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sent to {item.recipientCount}{" "}
                    {item.recipientCount === 1 ? "person" : "people"}.
                  </p>
                )}
              {item.status === "PENDING" && (
                <p
                  className="mt-1 text-xs text-muted-foreground"
                  data-testid="scheduled-pending"
                >
                  Still being scheduled. It appears here as Scheduled within
                  about two minutes; there is no need to schedule it again.
                </p>
              )}
              {!deleted && item.status === "FAILED" && item.error && (
                <p
                  className="mt-1 break-words text-xs text-destructive-text"
                  data-testid="scheduled-failure"
                >
                  {item.error}
                </p>
              )}
              {item.status === "SCHEDULED" && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className={ROW_BUTTON}
                    data-testid="scheduled-edit"
                    onClick={() => setEditing(item)}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={ROW_BUTTON}
                    data-testid="scheduled-cancel"
                    onClick={() => setCancelling(item)}
                  >
                    <X className="h-4 w-4" />
                    Cancel sending
                  </Button>
                </div>
              )}
              <DeletionActions
                deletion={item.deletion}
                timeZone={timeZone}
                canDelete={sendCommandId !== null}
                onDelete={() =>
                  sendCommandId && setDeleting({ item, sendCommandId })
                }
              />
            </li>
          );
        })}
      </ul>

      {editing && (
        <EditDialog
          key={editing.id}
          item={editing}
          eventId={eventId}
          uid={uid}
          timeZone={timeZone}
          forms={forms}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteForEveryoneDialog
          key={deleting.item.id}
          eventId={eventId}
          uid={uid}
          sendCommandId={deleting.sendCommandId}
          title={deleting.item.title}
          onClose={() => setDeleting(null)}
        />
      )}
      {cancelling && (
        <CancelDialog
          key={cancelling.id}
          item={cancelling}
          eventId={eventId}
          uid={uid}
          timeZone={timeZone}
          onClose={() => setCancelling(null)}
        />
      )}
    </section>
  );
}

function EditDialog({
  item,
  eventId,
  uid,
  timeZone,
  forms,
  onClose,
}: {
  item: ScheduledAnnouncement;
  eventId: string;
  uid: string;
  timeZone: string | undefined;
  forms: FeedbackForm[] | null;
  onClose: () => void;
}) {
  const { state, submit, reset } = useAdminCommand();
  const [title, setTitle] = useState(item.title);
  const [body, setBody] = useState(item.body);
  const [sendAt, setSendAt] = useState(
    instantToWallTime(item.sendAt, timeZone),
  );
  // Starts on the stored form; "No feedback form" removes it.
  const [formId, setFormId] = useState<string>(item.feedbackFormId ?? NO_FORM);
  // TNG-94: the seating {dinner_table} reads from, required while it is used.
  const [seatingId, setSeatingId] = useState<string>(
    item.seatingId ?? NO_SEATING,
  );
  const needsSeating = usesDinnerTable(title, body);
  const seatings = useSeatings(eventId, { enabled: needsSeating });
  const [problem, setProblem] = useState<string | null>(null);
  const busy = state.status === "submitting" || state.status === "watching";
  const parsedSendAt = parseSendTime(sendAt, timeZone, new Date());

  useEffect(() => {
    if (state.status === "succeeded") onClose();
  }, [state.status, onClose]);

  function save() {
    if (busy) return;
    if (!title.trim() || !body.trim()) {
      setProblem("Both the title and the message are needed.");
      return;
    }
    // The backend refuses the edit too; saying so here names the typo.
    const placeholderProblem = templateProblem(title) ?? templateProblem(body);
    if (placeholderProblem) {
      setProblem(placeholderProblem);
      return;
    }
    if (needsSeating && seatingId === NO_SEATING) {
      setProblem("Choose which seating {dinner_table} is filled from.");
      return;
    }
    const parsed = parseSendTime(sendAt, timeZone, new Date());
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    setProblem(null);
    if (formId !== NO_FORM && forms !== null && !findForm(forms, formId)) {
      setProblem(
        "The linked feedback form was deleted. Choose another one, or no form.",
      );
      return;
    }
    void submit("UPDATE_SCHEDULED_ANNOUNCEMENT", eventId, uid, {
      scheduledAnnouncementId: item.id,
      title: title.trim(),
      body: body.trim(),
      sendAt: parsed.instant,
      // Always named: null clears the form, and a missing key would keep
      // the stored one.
      feedbackFormId: formId === NO_FORM ? null : formId,
      // Always named too: null removes a seating the text no longer uses.
      seatingId: needsSeating && seatingId !== NO_SEATING ? seatingId : null,
    });
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit scheduled announcement</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="scheduled-edit-title">Title</Label>
          <Input
            id="scheduled-edit-title"
            testId="scheduled-edit-title"
            value={title}
            maxLength={120}
            disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="scheduled-edit-body">Message</Label>
          <Textarea
            id="scheduled-edit-body"
            data-testid="scheduled-edit-body"
            value={body}
            maxLength={1000}
            disabled={busy}
            rows={4}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <TimeField
          label="Send at"
          testId="scheduled-edit-send-at"
          value={sendAt}
          onChange={(value) => {
            setSendAt(value);
            setProblem(null);
          }}
        />
        <SendTimeNote
          timeZone={timeZone}
          wall={sendAt}
          testId="scheduled-edit-clock"
        />
        {needsSeating && (
          <SeatingPicker
            id="scheduled-edit-seating"
            testId="scheduled-edit-seating"
            seatings={seatings.status === "success" ? seatings.data : null}
            value={seatingId}
            onChange={(value) => {
              setSeatingId(value);
              setProblem(null);
            }}
            mode="later"
            disabled={busy}
          />
        )}
        <FeedbackFormPicker
          id="scheduled-edit-form"
          testId="scheduled-edit-form"
          forms={forms}
          value={formId}
          onChange={(value) => {
            setFormId(value);
            setProblem(null);
          }}
          mode="later"
          at={"instant" in parsedSendAt ? parsedSendAt.instant : null}
          timeZone={timeZone}
          disabled={busy}
        />
        {problem && (
          <p
            className="text-sm text-destructive-text"
            role="alert"
            data-testid="scheduled-edit-problem"
          >
            {problem}
          </p>
        )}
        <AdminCommandFeedback
          state={state}
          onReset={reset}
          testId="scheduled-edit"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="cta"
            disabled={busy}
            data-testid="scheduled-edit-save"
            onClick={save}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({
  item,
  eventId,
  uid,
  timeZone,
  onClose,
}: {
  item: ScheduledAnnouncement;
  eventId: string;
  uid: string;
  timeZone: string | undefined;
  onClose: () => void;
}) {
  const { state, submit, reset } = useAdminCommand();
  const busy = state.status === "submitting" || state.status === "watching";

  useEffect(() => {
    if (state.status === "succeeded") onClose();
  }, [state.status, onClose]);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel this announcement?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          &ldquo;{item.title}&rdquo; was due to go out on{" "}
          {formatSendTime(item.sendAt, timeZone)}. It will not be sent, and a
          cancelled announcement cannot be brought back.
        </p>
        <AdminCommandFeedback
          state={state}
          onReset={reset}
          testId="scheduled-cancel"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="destructive"
            disabled={busy}
            data-testid="scheduled-cancel-confirm"
            onClick={() =>
              void submit("CANCEL_SCHEDULED_ANNOUNCEMENT", eventId, uid, {
                scheduledAnnouncementId: item.id,
              })
            }
          >
            Cancel sending
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
